import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import uuid from 'uuid/v4';

import { Text, Title, Box } from '@nerdwallet/currency';
import classNames from 'classnames';
import classes from '@nerdwallet/base-styles/classes';

import { REGISTRATION_EMBEDS } from '@nerdwallet/js-auth-tools/registration';

import { ElementImpression } from '@nerdwallet/nw-client-lib/tracking';
import analytics from '@nerdwallet/analytics';
import { Impression } from '@nerdwallet/analytics/react/impression';
import { connect } from 'react-redux';
import {
  setTrk,
  TrkTag,
  TrkElement,
} from '@nerdwallet/nw-client-lib/trk-helper';
import { createStructuredSelector } from 'reselect';
// eslint-disable-next-line lodash/import-scope -- directive added automatically by Shepherd migration
import property from 'lodash/property';
// eslint-disable-next-line lodash/import-scope -- directive added automatically by Shepherd migration
import get from 'lodash/get';
import {
  stringTextAreaSpec,
  boolCheckboxSpec,
  subtitleInput,
  stringInputSpec,
  stringSelectSpec,
} from '../../lib/editing-configs';
import getTextColor from '../../lib/text-color-config';
import parseStyleFormatting from '../../lib/parse-style-formatting';
import parseMarkdownLinks from '../../lib/parse-md-links';

import BackgroundColor from '../background-color/background-color';
import SmartButton from '../smart-button/smart-button';
import LinkButton from '../link-button/link-button';

import './last-chance.less';

const linkParser = (content, allowHtml) =>
  allowHtml ? parseMarkdownLinks(content, false) : content;

const analyticsConstants = {
  trk: 'lpc',
  section: 'last_chance',
};

const LastChance = ({
  title,
  subtitle,
  bgOptions,
  fontChronicle,
  backgroundSize,
  backgroundImageSrcDesktop,
  backgroundImageSrcTablet,
  backgroundImageSrcMobile,
  size,
  desktopAlignment,
  mobileAlignment,
  backgroundImage,
  linkText,
  linkColor,
  dangerouslyAllowHTMLInSubtitle,

  // For SmartButton
  buttonURL,
  buttonText,
  textColorOptions,
  titleColorOptions,
  includeSignUpButtons,
  includeContentWidthLimitation,
  includeParentWithLimitation,
  isSignedIn,
  rootBgOptions,
  ...smartButtonProps
}) => {
  // Only append trk params if this is not an #anchor link on the
  // current page.
  //
  // @todo in the future we can extract this to a new maybeSetTrk()
  //     util if there are multiple use cases.
  const isAnchorLink = (buttonURL || '').charAt(0) === '#';
  const modifiedUrl = isAnchorLink
    ? buttonURL
    : buttonURL &&
      setTrk({
        url: buttonURL,
        params: {
          [TrkTag.location]: 'LastChance',
          [TrkTag.element]: TrkElement.button,
          [TrkTag.copy]: buttonText,
        },
      });

  const nodeId = uuid();
  // useState is used to ensure that the trkId is unique per instance
  // and will only be set/updated when the component initially renders
  const [trkId] = useState(nodeId);

  const elementImpressionProps = {
    id: `last-chance-${trkId}`,
    elementName: 'LastChance',
  };

  const { textColor } = getTextColor(textColorOptions);
  const colorStyle = { color: textColor };

  const { textColor: titleTextColor } = getTextColor(titleColorOptions);
  const titleColorStyle = { color: titleTextColor };

  const lastChanceImageSrcMap = {
    lg: backgroundImageSrcDesktop,
    md: backgroundImageSrcTablet,
    sm: backgroundImageSrcMobile,
  };

  const htmlOutputParser = (subtitleContent) => {
    if (dangerouslyAllowHTMLInSubtitle) {
      // eslint-disable-next-line react/no-danger -- directive added automatically by Shepherd migration
      return <span dangerouslySetInnerHTML={{ __html: subtitleContent }} />;
    }
    return linkParser(subtitleContent, dangerouslyAllowHTMLInSubtitle);
  };

  const signUpRef = useRef(null);

  useEffect(() => {
    if (!signUpRef.current || !includeSignUpButtons || isSignedIn) {
      return;
    }

    const redirectToDashboard = () => {
      if (window) {
        window.location = '/home/dashboard/home';
      }
    };

    const openRegisterAuthDialogHelper = () => {
      window?.nwapi?.ui?.showAuthDialog({
        authType: 'register',
        version: 2,
        trk: analyticsConstants.trk,
        flowConfig: {
          errorToastPositionMobile: 'bottom-center',
        },
        onSuccessActions: redirectToDashboard,
      });
    };

    window.nwapi?.ui?.showAuthEmbed({
      el: signUpRef.current,
      authType: 'register',
      flowConfig: {
        enableSuccessErrorModals: false,
        openRegisterAuthDialog: openRegisterAuthDialogHelper,
      },
      regFlow: REGISTRATION_EMBEDS.AWARDS_PAGE_EMBED,
      version: 2,
      flow: 'no-security-question',
      trk: analyticsConstants.trk,
      regTrackingAttributes: {
        entityNames: {
          authButtons: 'auth_button',
        },
        sectionName: analyticsConstants.section,
      },
      onSuccess() {
        analytics.track('element_impression', {
          entity_name: 'success_modal',
          section_name: analyticsConstants.section,
        });
        redirectToDashboard();
      },
      onFailure() {
        analytics.track('element_impression', {
          entityName: 'error_modal',
          sectionName: analyticsConstants.section,
        });
      },
    });
  }, [includeSignUpButtons, isSignedIn]);

  return includeSignUpButtons && isSignedIn ? null : (
    <BackgroundColor bgOptions={rootBgOptions}>
      {(rootBackgroundColor) => (
        <Impression
          eventType="element_impression"
          entityName="last_chance"
          className={classNames('last-chance__wrapper', {
            '_not-wide': includeParentWithLimitation,
          })}
          style={{
            backgroundColor: rootBackgroundColor,
          }}
        >
          <ElementImpression {...elementImpressionProps}>
            <BackgroundColor bgOptions={bgOptions}>
              {(backgroundColor) => (
                <div
                  className={classNames('last-chance', {
                    '_not-wide': includeParentWithLimitation,
                  })}
                  style={{
                    backgroundColor,
                    backgroundImage:
                      backgroundImage && `url(${lastChanceImageSrcMap[size]})`,
                    backgroundSize: backgroundImage && `${backgroundSize}`,
                    backgroundPosition:
                      size && size !== 'sm'
                        ? `${desktopAlignment}`
                        : `${mobileAlignment}`,
                  }}
                >
                  <Box
                    className={classNames([
                      'last-chance__content',
                      'centered-in-parent',
                      { '_two-thirds': includeContentWidthLimitation },
                    ])}
                  >
                    <Title
                      size="small"
                      className={classNames([
                        'last-chance__title',
                        {
                          gotham: !fontChronicle,
                        },
                      ])}
                      style={titleColorStyle}
                    >
                      {parseStyleFormatting(title)}
                    </Title>
                    <Box style={colorStyle}>
                      <Text>{htmlOutputParser(subtitle)}</Text>
                    </Box>
                    <Box
                      className={classNames([
                        classes('padding-top-4'),
                        classes('padding-bottom-5--wide'),
                        classes('padding-bottom-4'),
                      ])}
                    >
                      {includeSignUpButtons && (
                        <div
                          className="last-change__sign-up centered-in-parent"
                          ref={signUpRef}
                        />
                      )}
                      <SmartButton
                        buttonURL={modifiedUrl}
                        buttonText={buttonText}
                        buttonClass="last-chance__button"
                        {...smartButtonProps}
                      />
                      <LinkButton linkText={linkText} color={linkColor} />
                    </Box>
                  </Box>
                </div>
              )}
            </BackgroundColor>
          </ElementImpression>
        </Impression>
      )}
    </BackgroundColor>
  );
};

LastChance.displayName = 'LastChance';

LastChance.propTypes = {
  title: PropTypes.string,
  subtitle: PropTypes.string,
  ...BackgroundColor.propTypes,
  textColor: PropTypes.string,
  fontChronicle: PropTypes.bool,
  ...SmartButton.propTypes,

  backgroundImage: PropTypes.bool,
  backgroundSize: PropTypes.oneOf(['contain', 'cover', 'auto']),
  desktopAlignment: PropTypes.oneOf(['left', 'center', 'right']),
  mobileAlignment: PropTypes.oneOf(['top', 'center', 'bottom']),
  backgroundImageSrcDesktop: PropTypes.string,
  backgroundImageSrcTablet: PropTypes.string,
  backgroundImageSrcMobile: PropTypes.string,
  dangerouslyAllowHTMLInSubtitle: PropTypes.bool,
  includeSignUpButtons: PropTypes.bool,
  includeContentWidthLimitation: PropTypes.bool,
  includeParentWithLimitation: PropTypes.bool,
  rootBgOptions: BackgroundColor.bgOptions,
};

LastChance.defaultProps = {
  buttonType: 'custom',
  fontChronicle: false,
  backgroundSize: 'contain',
  size: '',
  backgroundImageSrcDesktop: '',
  backgroundImageSrcTablet: '',
  backgroundImageSrcMobile: '',
  desktopAlignment: 'center',
  mobileAlignment: 'center',
  backgroundImage: false,
  dangerouslyAllowHTMLInSubtitle: false,
  includeSignUpButtons: false,
  includeContentWidthLimitation: false,
  includeParentWithLimitation: false,
  rootBgOptions: BackgroundColor.defaultProps.bgOptions,
  ...BackgroundColor.defaultProps,
  ...SmartButton.defaultProps,
};

const lastChanceImageTypeInput = [
  boolCheckboxSpec('backgroundImage', 'Enable background Image?', {
    help: 'If checked enables background image output.',
  }),
  stringSelectSpec(
    'backgroundSize',
    'Background Size',
    [
      { label: 'Contain', value: 'contain' },
      { label: 'Cover', value: 'cover' },
      { label: 'Auto', value: 'auto' },
    ],
    {
      help: 'Only applies to Desktop and Tablet',
    }
  ),
  stringSelectSpec(
    'desktopAlignment',
    'Desktop Alignment',
    [
      { label: 'Left', value: 'left' },
      { label: 'Center', value: 'center' },
      { label: 'Right', value: 'right' },
    ],
    {
      help: 'Only applies to Desktop and Tablet',
    }
  ),
  stringSelectSpec(
    'mobileAlignment',
    'Mobile Alignment',
    [
      { label: 'Top', value: 'top' },
      { label: 'Middle', value: 'center' },
      { label: 'Bottom', value: 'bottom' },
    ],
    {
      help: 'Only applies to Mobile',
    }
  ),
  stringInputSpec(
    'backgroundImageSrcDesktop',
    'Background Image URL (Desktop)'
  ),
  stringInputSpec('backgroundImageSrcTablet', 'Background Image URL (Tablet)'),
  stringInputSpec('backgroundImageSrcMobile', 'Background Image URL (Mobile)'),
];

LastChance.getEditingConfig = (componentDocument) => {
  const lastChanceImageTypeInputConfig =
    get(componentDocument, 'props.backgroundImage', false) === true
      ? [...lastChanceImageTypeInput]
      : [lastChanceImageTypeInput[0]];
  return [
    ...BackgroundColor.getEditingConfig(componentDocument),
    ...lastChanceImageTypeInputConfig,
    stringTextAreaSpec('title', 'Title', {
      help: 'For GothamBlack font, surround title text with | like |this|',
    }),
    ...getTextColor.getEditingConfig(
      componentDocument,
      'titleColorOptions',
      'Title Color'
    ),
    boolCheckboxSpec('fontChronicle', 'Title in Chronicle?'),
    subtitleInput,
    boolCheckboxSpec(
      'dangerouslyAllowHTMLInSubtitle',
      'Allow HTML in Subtitle?',
      {
        help: 'WARNING: We strongly advise against making use of this feature unless you really need it.',
      }
    ),
    boolCheckboxSpec('includeSignUpButtons', 'Add Sign Up buttons?'),
    boolCheckboxSpec(
      'includeContentWidthLimitation',
      'Output Header and Body 2/3 width?'
    ),
    boolCheckboxSpec(
      'includeParentWithLimitation',
      'Limit width of container to centered-in-parent?'
    ),
    ...(get(componentDocument, 'props.includeParentWithLimitation', false)
      ? BackgroundColor.getEditingConfig(
          componentDocument,
          'rootBgOptions',
          'Root background color'
        )
      : []),
    ...getTextColor.getEditingConfig(
      componentDocument,
      'textColorOptions',
      'Subtitle Color'
    ),
    ...SmartButton.getEditingConfig(componentDocument),
    ...LinkButton.getEditingConfig(componentDocument),
  ];
};

export default connect(
  createStructuredSelector({
    size: property('mediaQuery'),
    isSignedIn: property('identity.isSignedIn'),
  })
)(LastChance);
